/* eslint-disable no-console */
import React from 'react';
import Icon from '@mdi/react';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { Typography, Grid } from '@mui/material';
import { Alert, Chip } from 'daaas-ui';
import { useTranslation } from 'react-i18next';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import LockIcon from '@mui/icons-material/Lock';
import {
  mdiTable,
  mdiWaves,
  mdiDatabase,
  mdiFactory,
  mdiWall,
  mdiFlask,
  mdiMonitorDashboard,
  mdiTransitConnectionVariant,
} from '@mdi/js';

// Components
import Banner from '../components/UI/Banner';
import ContentContainer from '../components/Layout/ContentContainer';
import ContentSection from '../components/Layout/ContentSection';
import ToolCard from '../components/UI/ToolCard';
import BrandingDAS from '../components/Layout/BrandingDAS';

// Logos
import chartBoxOutline from '../assets/images/chartBoxOutline.png';
import squigglyShapes from '../assets/images/squigglyShapes.svg';

const Root = styled('div')(({ theme }) => ({
  '.MuiChip-outlinedPrimary': {
    border: '3px solid',
    borderColor: `${theme.palette.primary.main} !important`,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
    borderTopLeftRadius: theme.spacing(0.625),
    borderTopRightRadius: theme.spacing(0.625),
    borderBottomLeftRadius: theme.spacing(0.625),
    borderBottomRightRadius: theme.spacing(0.625),
    '& .MuiChip-label': {
      fontWeight: 'bolder',
      fontSize: '1rem',
    },
  },
  '& .banner__header': {
    fontSize: `3.5rem !important`,
    lineHeight: '1.275',
    letterSpacing: '0.47',
    [theme.breakpoints.up('sm')]: {
      fontSize: `5rem !important`,
    },
  },
  '& .cae__squigglyShapes': {},
}));

export default function CAE() {
  const { t, i18n } = useTranslation(['cae', 'common']);

  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const statCanDomains = ['statcan.gc.ca', 'cloud.statcan.ca'];
  const isStatCanAuthenticated =
    isAuthenticated &&
    instance.getActiveAccount() &&
    statCanDomains.indexOf(
      instance.getActiveAccount().username.split('@')[1].toLowerCase()
    ) !== -1;

  React.useEffect(() => {
    setTimeout(() => {
      document.title = `${t('common:daaas')} - ${t('common:cae')}`;
      document
        .querySelector("meta[name='description']")
        .setAttribute('content', t('cae:description'));
      document
        .querySelector("meta[name='keywords']")
        .setAttribute('content', t('cae:keywords'));
      document
        .querySelector("link[rel='canonical']")
        .setAttribute('href', t('cae:canonical'));
      document
        .querySelector("meta[name='dcterms.title']")
        .setAttribute('content', t('cae:dctermsTitle'));
      document
        .querySelector("meta[name='dcterms.description']")
        .setAttribute('content', t('cae:dctermsDescription'));
      document
        .querySelector("meta[name='dcterms.creator']")
        .setAttribute('content', t('common:dctermsCreator'));
      document
        .querySelector("meta[name='dcterms.language']")
        .setAttribute('content', t('common:dctermsLanguage'));
      document
        .querySelector("meta[name='dcterms.subject']")
        .setAttribute('content', t('common:dctermsSubject'));
    }, '100');
  }, [i18n.language]);

  return (
    <Root>
      <Banner>
        <Typography
          className="banner__header"
          variant="h1"
          color="textPrimary"
          sx={{ py: 2 }}
        >
          {t('common:cae')}
        </Typography>
      </Banner>
      <ContentContainer paddingBottom={false}>
        <ContentSection>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t('bannerH1')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('bannerPContent')}
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">{t('bannerBullet1')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet2')}</Typography>
            </li>
            <li>
              <Typography variant="body1">{t('bannerBullet3')}</Typography>
            </li>
          </ul>
          <Alert open severity="info" closeable={false} sx={{ mt: 2, mb: 3 }}>
            <Typography variant="inherit" sx={{ mb: 2 }}>
              {t('alertText1')}
            </Typography>
            <Typography variant="inherit">
              {t('alertText2')}
              <Link
                href={t('alertHREF')}
                rel="noopener noreferrer"
                color="inherit"
              >
                {t('alertLink')}
              </Link>
              {t('alertPeriod')}
            </Typography>
          </Alert>
          <Grid container wrap="nowrap" alignItems="center">
            <Grid item>
              <Chip
                color="primary"
                label={t('common:protectedB')}
                icon={<LockIcon />}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: 'bold' }}
              >
                {t('caeToolTip')}
              </Typography>
            </Grid>
          </Grid>
        </ContentSection>
        <ContentSection
          heading={t('dataStorageHeading')}
          subheading={t('dataStorageSubHeading')}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureBlobStorageHeading'),
                  linkTo: isStatCanAuthenticated
                    ? t('cae:azureBlobStorageLink')
                    : '',
                }}
                links={[
                  {
                    linkTo: t('azureBlobStorageInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureBlobStorageHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiTable} />}
              >
                <Typography variant="body1">
                  {t('azureBlobStorageParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureDataLakesHeading'),
                  linkTo: isStatCanAuthenticated
                    ? t('cae:azureBlobStorageLink')
                    : '',
                }}
                links={[
                  {
                    linkTo: t('azureDataLakesInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureDataLakesHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiWaves} />}
              >
                <Typography variant="body1">
                  {t('azureDataLakesParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureSQLDatabaseHeading'),
                  linkTo: isStatCanAuthenticated
                    ? t('cae:azureSQLDatabaseLink')
                    : '',
                }}
                links={[
                  {
                    linkTo: t('azureSQLDatabaseInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureSQLDatabaseHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiDatabase} />}
              >
                <Typography variant="body1">
                  {t('azureSQLDatabaseParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
          </Grid>
        </ContentSection>
        <ContentSection
          heading={t('dataPreparationHeading')}
          subheading={t('cleanOrganizeFormatSubHeading')}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureDataFactoryHeading'),
                  linkTo: t('azureDataFactoryLink'),
                }}
                links={[
                  {
                    linkTo: t('azureDataFactoryInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureDataFactoryHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiFactory} />}
              >
                <Typography variant="body1">
                  {t('azureDataFactoryParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={0} md={0} lg={8} sx={{ alignSelf: 'center' }}>
              <img
                src={squigglyShapes}
                alt=""
                className="cae__squigglyShapes"
                loading="lazy"
              />
            </Grid>
          </Grid>
        </ContentSection>
        <ContentSection
          heading={t('dataAnalysisAndVisualizationHeading')}
          subheading={t('shareInsightsAndDataStoriesSubHeading')}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureSynapseAnalyticsHeading'),
                  linkTo: t('azureSynapseAnalyticsLink'),
                }}
                links={[
                  {
                    linkTo: t('azureSynapseAnalyticsInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureSynapseAnalyticsHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiTransitConnectionVariant} />}
              >
                <Typography variant="body1">
                  {t('azureSynapseAnalyticsParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureDatabricksHeading'),
                  linkTo: t('cae:azureDatabricksLink'),
                }}
                links={[
                  {
                    linkTo: t('azureDatabricksInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureDatabricksHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiWall} />}
              >
                <Typography variant="body1">
                  {t('azureDatabricksParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('powerBIHeading'),
                  linkTo: t('cae:powerBILink'),
                }}
                icon={<img src={chartBoxOutline} alt="" loading="lazy" />}
              >
                <Typography variant="body1">{t('powerBIParagraph')}</Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureMachineLearningHeading'),
                  linkTo: t('cae:azureMachineLearningLink'),
                }}
                links={[
                  {
                    linkTo: t('azureMachineLearningInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureMachineLearningHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiFlask} />}
              >
                <Typography variant="body1">
                  {t('azureMachineLearningParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                heading={{
                  text: t('azureVirtualMachinesHeading'),
                  linkTo: isStatCanAuthenticated
                    ? t('cae:azureVirtualMachinesLink')
                    : '',
                }}
                links={[
                  {
                    linkTo: t('azureVirtualMachinesInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('azureVirtualMachinesHeading')}
                        </span>
                      </>
                    ),
                  },
                ]}
                icon={<Icon path={mdiMonitorDashboard} />}
              >
                <Typography variant="body1">
                  {t('azureVirtualMachinesParagraph')}
                </Typography>
              </ToolCard>
            </Grid>
          </Grid>
        </ContentSection>
      </ContentContainer>
      <BrandingDAS />
    </Root>
  );
}
